/**
 * Slider — input range stylé avec affichage optionnel de la valeur.
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Affiche la valeur courante à droite du slider. */
  showValue?: boolean;
  /** Formateur custom (ex: ajout d'unité "px", "%"). */
  valueFormatter?: (value: number) => string;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(function Slider(
  {
    value = 0,
    min = 0,
    max = 100,
    step = 1,
    showValue = false,
    valueFormatter = (v) => String(v),
    className,
    ...rest
  },
  ref,
) {
  const numericValue = typeof value === 'number' ? value : Number(value);

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <input
        ref={ref}
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        className={cn(
          'bg-bg-muted h-1.5 flex-1 cursor-pointer appearance-none rounded-full',
          // Thumb (Webkit + Firefox)
          '[&::-webkit-slider-thumb]:bg-primary-500 [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110',
          '[&::-moz-range-thumb]:bg-primary-500 [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...rest}
      />
      {showValue && (
        <span className="text-fg-muted min-w-10 text-right text-xs tabular-nums">
          {valueFormatter(numericValue)}
        </span>
      )}
    </div>
  );
});
